<template>
    <div class="relative z-10 " ref="confirmReportModal">
        <button class="th-btn" @click="handleOpenConfirm">
            <IconOutputReport class="th-btn-icon"/>
            <span>
                {{ $t('generateReport') }} ({{ userCheckedReportTotal }})
            </span>
        </button>
        <div  v-show="confirmReportPopover"  class="th-menu absolute right-0 z-10 mt-1 w-[600px] px-9 pb-6 pt-9">
            <p class="mb-6 text-lg font-semibold text-black">
                {{tips}}
            </p>

            <div class="mb-3 flex items-center justify-center gap-2 rounded border-b border-dotted border-grey-55 py-3 text-sm text-grey-450" v-if="!userCheckedReportTotal">
                <CatHole2 class="h-8 w-11"/>
                {{$t('systemAddListTip')}}
            </div>

            <div class="max-h-[330px] overflow-y-auto" v-if="userConfirmReportList.length">
                <draggable
                    v-model="reportList"
                    item-key="patent_id">
                    <template #item="{element}">
                        <div  class="group mb-1 flex cursor-pointer items-center justify-between p-2 text-sm text-black last:mb-0 hover:rounded hover:bg-grey-hover">
                            <div class="th-btn th-btn-text mr-3 shrink-0 px-1">
                                <IconDraggable class="th-btn-icon !text-grey-450"/>
                            </div>
                            <span class="mr-1 grow flex-wrap" v-html="`${element.order}.${element.title}`"></span>
                            <div v-if="!disableRemove" class="th-btn th-btn-plain invisible shrink-0 px-0 group-hover:visible" @click="()=>removeChecked(element)">
                                <IconDelete class="th-btn-icon !text-grey-450"/>
                            </div>
                        </div>
                    </template>
                </draggable>
            </div>

            <div class="mt-6 flex justify-end gap-3">
                <button class="th-btn th-btn-outline" @click="handleCancel">
                    {{$t('cancel')}}
                </button>
                <button class="th-btn" @click="handleSubmit">
                    {{ $t('submit') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import IconOutputReport from '@/components/icons/IconOutputReport.vue'
import IconDraggable from '@/components/icons/IconDraggable.vue'
import draggable from 'vuedraggable'
import type { IBaseSolution } from '@/types';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import IconDelete from '@/components/icons/IconDelete.vue'
import { CatHole2 } from '@eureka/cat';
import { cloneDeep, remove } from 'lodash'
import { onClickOutside } from '@vueuse/core'
const confirmReportModal = ref(null)

const { t } = useI18n()

const props = defineProps<{
    userCheckedReportTotal?: number;
    userConfirmReportList: IBaseSolution[]
}>();
const emits = defineEmits<{
    (e: 'update:handleSubmit', value: IBaseSolution[]): void
    (e: 'update:handleRemove', item: IBaseSolution): void
}>()

const confirmReportPopover = ref(false)
const reportList = ref<IBaseSolution[]>([])

const tips = computed(() => {
    return props.userCheckedReportTotal ? t('manuallyGenerateTip', { index: props.userConfirmReportList.length }) : t('systemGenerateTip')
})

const disableRemove = computed(() => {
    return reportList.value.length < 2
})

onClickOutside(
    confirmReportModal,
    () => {
        confirmReportPopover.value = false
    }
)

function handleOpenConfirm() {
    confirmReportPopover.value = !confirmReportPopover.value
    if (confirmReportPopover.value) {
        reportList.value = props.userCheckedReportTotal ? props.userConfirmReportList : cloneDeep(props.userConfirmReportList)
    }
}

function handleCancel() {
    confirmReportPopover.value = false
}

function handleSubmit() {
    if (!reportList.value.length) {
        return
    }
    confirmReportPopover.value = false
    emits('update:handleSubmit', reportList.value)
}

function removeChecked(item: IBaseSolution) {
    if (disableRemove.value) return
    if (props.userCheckedReportTotal) {
        remove(reportList.value, r => r.patent_id === item.patent_id)
        emits('update:handleRemove', item)
    } else {
        remove(reportList.value, r => r.patent_id === item.patent_id)
    }
}
</script>
